import React from 'react';
import PropTypes from 'prop-types';
import { notice, alert } from 'ducks/notifications';
import Component from 'BaseComponent';
import api from 'api';
import autobind from 'autobind-decorator';
import { query, invalidateRequests } from 'redux-bees';
import ImportWpressSite from 'components/ImportWpressSite';
import CreateSite from 'components/CreateSite';
import { withRouter, Switch, Route, Link, Redirect } from 'react-router-dom';
import Modal from 'components/Modal';
import Time from 'components/Time';
import Spinner from 'components/Spinner';
import ProgressBar from 'components/ProgressBar';
import addSeconds from 'date-fns/addSeconds';
import WpSwitch from 'components/WpSwitch';
import OperationButton from 'components/OperationButton';
import Pagination from 'components/Pagination';
import Search from 'components/Search';

const toRenderProp = ({ children, ...rest }) => children(rest);
const createQueryProvider = (...args) => query(...args)(toRenderProp);
const PaginationProvider = createQueryProvider('sites', api.getSites, (perform, props) => {
  if (props.params.search !== '') {
    return perform({ page: props.params.page, search: props.params.search });
  }
  return perform({ page: props.params.page });
});

@withRouter

@query('account', api.getAccount)
@query('billingProfile', api.getBillingProfile)

export default class Sites extends Component {
  static propTypes = {
    billingProfile: PropTypes.object,
    account: PropTypes.object,
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
  }

  state = { page: '1', search: '' }

  @autobind
  handlePageChange(page) {
    this.setState({ page });
  }

  @autobind
  handleSearch(search) {
    this.setState({ page: 1, search });
  }

  @autobind
  handleCloseModal() {
    this.props.history.push('/');
  }

  @autobind
  handleSiteReactivation(site) {
    const { dispatch } = this.props;
    return dispatch(api.reactivateSite({ id: site.id }, null))
    .then(() => dispatch(notice('Site.reactivationSuccessful')))
    .then(() => dispatch(invalidateRequests(api.getSites)))
    .catch(() => dispatch(alert('Site.reactivationFailure')));
  }

  @autobind
  handleTrialReactivation() {
    const { dispatch } = this.props;
    return dispatch(api.extendTrial())
    .then(() => dispatch(notice('Site.reactivationSuccessful')))
    .then(() => dispatch(invalidateRequests(api.getAccount)))
    .then(() => dispatch(invalidateRequests(api.getSites)))
    .catch(() => dispatch(alert('Site.reactivationFailure')));
  }

  @autobind
  renderProgressBars(site) {
    return (
      <div>
        {
          site.attributes.wpState === 'deploying' &&
            <div>
              <div className="Sites__item__details">
                Publishing the site...
              </div>
              <div className="space--top-1">
                <ProgressBar
                  startedAt={site.attributes.lastDeployStartedAt}
                  expectedFinishDate={
                    addSeconds(
                      site.attributes.lastDeployStartedAt,
                      site.attributes.averageDeployTimeInSeconds,
                    ).toISOString()
                  }
                />
              </div>
            </div>
        }
        {
          site.attributes.wpState === 'loading' &&
            <div>
              <div className="Sites__item__details">
                Switching WordPress ON ...
              </div>
              <div className="space--top-1">
                <ProgressBar
                  startedAt={site.attributes.startedAt}
                  expectedFinishDate={
                    addSeconds(
                      site.attributes.startedAt,
                      site.attributes.averageStartTimeInSeconds,
                    ).toISOString()
                  }
                />
              </div>
            </div>
        }
        {
          site.attributes.wpState === 'pausing' &&
            <div>
              <div className="Sites__item__details">
                Switching WordPress OFF ...
              </div>
              <div className="space--top-1">
                <ProgressBar
                  startedAt={site.attributes.pausedAt}
                  expectedFinishDate={
                    addSeconds(
                      site.attributes.pausedAt,
                      site.attributes.averagePauseTimeInSeconds,
                    ).toISOString()
                  }
                />
              </div>
            </div>
        }
      </div>
    );
  }

  @autobind
  renderSite(site) {
    const { account } = this.props;
    const url = site.attributes.screenshotUrl;
    const siteName = site.attributes.name;

    const image = url ?
      <img className="Sites__item__image" alt="Site screenshot" src={url} /> :
      (
        <div className="Sites__item__image-placeholder">
          <Spinner />
        </div>
      );

    if (site.attributes.wpState === 'installing') {
      return (
        <div
          key={site.id}
          className="Sites__item"
        >
          {image}
          <div className="Sites__item__content">
            <div className="Sites__item__name">
              {siteName}
            </div>
            <div className="Sites__item__details">
              Installing WordPress...
            </div>
            <div className="space--top-1">
              <ProgressBar
                startedAt={site.attributes.createdAt}
                expectedFinishDate={addSeconds(site.attributes.createdAt, 30).toISOString()}
              />
            </div>
          </div>
        </div>
      );
    }

    if (site.attributes.wpState === 'importing') {
      return (
        <div
          key={site.id}
          className="Sites__item"
        >
          <Link
            to={`/site/${site.id}`}
            className="Sites__item__link"
          >
            {image}
            <div className="Sites__item__content">
              <div className="Sites__item__name">
                {siteName}
              </div>
              <div className="Sites__item__details">
                Importing existing site...
              </div>
              <div className="space--top-1">
                <ProgressBar
                  startedAt={site.attributes.createdAt}
                  expectedFinishDate={addSeconds(site.attributes.createdAt, 30).toISOString()}
                />
              </div>
            </div>
          </Link>
        </div>
      );
    }
    if (site.attributes.wpState === 'deactivated') {
      return (
        <div
          className="Sites__item"
          key={site.id}
        >
          {image}
          <div className="Sites__item__content">
            <div className="Sites__item__name">
              {siteName}
            </div>
            <div className="space--bottom-1">
              <div className="Sites__item__details">
                Last deploy <Time value={site.attributes.lastDeployAt} />
              </div>
              <div className="Sites__item__deactivated" >
                Site deactivated
              </div>
            </div>
          </div>
          <div className="Sites__item__actions">
            {!account.attributes.isDeactivated &&
              <div>
                <OperationButton
                  className="button"
                  onClick={() => { return this.handleSiteReactivation(site); }}
                  label={['Reactivate']}
                />
                <div className="space--bottom-1" />
              </div>
            }

          </div>
        </div>
      );
    }

    if (site.attributes.wpState === 'imported') {
      return (
        <div
          key={site.id}
          className="Sites__item"
        >
          <Link
            to={`/site/${site.id}`}
            className="Sites__item__link"
          >
            {image}
            <div className="Sites__item__content">
              <div className="Sites__item__name">
                {siteName}
              </div>
              <div className="Sites__item__details">
                Manual review in progress
              </div>
            </div>
          </Link>
        </div>
      );
    }

    return (
      <div
        className="Sites__item"
        key={site.id}
      >
        <Link
          to={`/site/${site.id}`}
          className="Sites__item__link"
        >
          {image}
          <div className="Sites__item__content">
            <div className="Sites__item__name">
              {siteName}
            </div>
            {
              site.attributes.wpState !== 'deploying' && site.attributes.wpState !== 'loading' && site.attributes.wpState !== 'pausing' && site.attributes.lastDeployAt &&
                <div className="space--bottom-1">
                  <div className="Sites__item__details">
                    Last deploy <Time value={site.attributes.lastDeployAt} />
                  </div>
                  <div className="Sites__item__fakeprogressbar" />
                </div>
            }
            { this.renderProgressBars(site) }
          </div>
        </Link>
        <div className="Sites__item__actions">
          <WpSwitch site={site} showText={false} />
        </div>
      </div>
    );
  }

  renderLoading() {
    return (
      <div className="Sites__spinner-container">
        <Spinner over />
      </div>
    );
  }

  renderSites(sites, status) {
    const { billingProfile, account } = this.props;
    const renderCancelled = account.attributes.isDeactivated === true;

    if (sites && sites.length === 0 && this.state.search === '') {
      return (
        <div>
          {
            billingProfile && renderCancelled &&
              this.renderCancelled()
          }
          {
            !renderCancelled &&
              <div className="blank-slate blank-slate--no-border blank-slate--top-no-padding">
                <p className="blank-slate__title">
                  Nice to see you :) Time to import your first site!
                </p>
                <p className="blank-slate__description" />
                <div className="blank-slate blank-slate--tiny">
                  <div className="Sites__empty_placeholder">
                    <ImportWpressSite />
                  </div>
                </div>
                <p className="blank-slate__description" />
                <div>
                  you can also
                  { ' '}
                  <Link
                    to="/sites/create"
                  >
                    <span>create an empty new site</span>
                  </Link>
                </div>
                <p className="blank-slate__description" />
                <div>
                  Do you want to import you local installation?
                  { ' '}
                  <a href="https://www.hardypress.com/guides/how-to-migrate-a-local-installation-on-hardypress/" target="_blank" rel="noopener noreferrer">Read our guide</a>

                </div>
              </div>
          }
        </div>
      );
    }

    return (
      <div>
        <div className="Backend__title space--bottom-2">
          Your sites

          <div className="Backend__title__extra">

            { (account.attributes.sitesCounter > 5 || account.attributes.isAdmin) &&
            <div className="Sites__search">
              <Search handleSearch={this.handleSearch} />
            </div>
            }

            <Link to="/sites/import" className="button--primary">
              <i className="icon--upload" />
              <span>Import an existing site</span>
            </Link>
            <Link to="/sites/create" className="button--primary">
              <i className="icon--add" />
              <span>Create a new site</span>
            </Link>
          </div>
        </div>
        {
          billingProfile && renderCancelled &&
            this.renderCancelled()
        }
        <div className="Sites__list">
          {
            status.sites.isLoading && this.renderLoading()
          }
          {sites && sites.length > 0 && sites.map(this.renderSite)}
          {sites && sites.length === 0 && this.state.search !== '' &&
            <div className="center space--top-3 space--bottom-3">Site not found!</div>
          }
        </div>
      </div>
    );
  }

  @autobind
  renderCancelled() {
    const { billingProfile, account } = this.props;

    return (
      <div className="Account__area">
        <div className="Account__area__content">
          <div className="Account__profile">
            <div className="trial">
              <div className="trial__title">
                Your subscription is <strong>expired</strong>
              </div>
              <div className="trial__description">
                Your sites have been deactivated. To reactivate them
                please insert your billing info, add a valid credit card
                and choose your plan.
              </div>
              <div className="trial__buttons">
                {
                  !billingProfile.attributes.addressLine &&
                    <Link to={'/account/billing_profiles/edit'} className="button">Insert billing info</Link>
                }
                {
                  !billingProfile.attributes.cardMaskedNumber &&
                    <Link to={'/account/billing_profiles/edit_card'} className="button">Add credit card</Link>
                }
                {
                  billingProfile.attributes.addressLine &&
                  billingProfile.attributes.cardMaskedNumber &&
                    <Link
                      to={'/account/billing_profiles/change_plan'}
                      className="button"
                    >
                      Choose plan/recurrence
                    </Link>
                }
                <div className="Account__trial-link">
                  <a href="https://www.hardypress.com/faq/redeem-a-coupon/" target="_blank" rel="noopener noreferrer">Do you have a coupon?</a>
                </div>

                { account.attributes.trialExtensionAllowed &&
                  <div className="trial__reactivate">
                    <div className="trial__reactivate__description">
                      Do you need more time to test HardyPress or curious to try out new features??
                    </div>
                    <OperationButton
                      className="button button--tiny"
                      onClick={() => { return this.handleTrialReactivation(); }}
                      label="Extend your trial for 7 additional days"
                    />
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div>
        <PaginationProvider params={this.state}>
          { ({ sites, status }) => (
            <div>
              <div>
                <div>
                  { this.renderSites(sites, status) }
                  {
                    sites &&
                      <Pagination
                        data={status.sites.meta.pagination}
                        handlePageChange={this.handlePageChange}
                      />
                  }
                </div>
              </div>

              <Switch>
                <Route
                  exact
                  path="/sites/import"
                  render={() => (
                    <Modal wide title="Import existing WordPress site" onClose={this.handleCloseModal}>
                      <ImportWpressSite />
                    </Modal>
                  )}
                />
                <Route
                  exact
                  path="/sites/create"
                  render={() => (
                    <Modal wide title="Create new WordPress site" onClose={this.handleCloseModal}>
                      <CreateSite />
                    </Modal>
                  )}
                />

                <Redirect to="/sites" />
              </Switch>
            </div>
          )}
        </PaginationProvider>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/components/Sites.jsx